/**
 * PBS Revision Control Types
 * Defines data structures for multi-level design maturity tracking
 */

// Design maturity stages
export type PbsRevisionStage =
    | 'alpha-1'   // First Prototype
    | 'alpha-2'   // Second Prototype  
    | 'beta-1'    // Engineering Prototype (DVT)
    | 'beta-2'    // Refined Engineering (PVT)
    | 'c-level'   // Customer Samples
    | 'p-level';  // Production Intent

// Revision level status
export type PbsRevisionStatus =
    | 'planning'  // Preparation phase
    | 'building'  // Assembly in progress
    | 'testing'   // Validation/testing
    | 'complete'  // Finished and archived
    | 'archived'; // Historical record

// Review status for carried-over items
export type PbsReviewStatus =
    | 'pending'    // Needs review (show RED)
    | 'confirmed'  // Reviewed and approved (show GREEN)
    | 'modified';  // Changed from original (show YELLOW)

/**
 * PBS Revision Level
 * Represents a specific build/design maturity level
 */
export interface PbsRevisionLevel {
    id: string;                      // UUID
    name: string;                    // e.g., "Alpha 1", "Beta 2"
    stage: PbsRevisionStage;         // Maturity level
    createdDate: string;             // ISO 8601
    createdBy: string;               // User initials
    description: string;             // Build purpose/notes
    carriedOverFrom?: string;        // Previous revision level ID
    isActive: boolean;               // Currently selected revision
    status: PbsRevisionStatus;       // Lifecycle status

    // Metadata
    projectName: string;            // Project this revision belongs to
    itemCount?: number;              // Number of PBS items in this revision
}

/**
 * Enhanced PBS Item with Revision Control
 */
export interface PbsItemRevision {
    // Core identification
    id: string;
    pbsNumber: string;
    name: string;
    type: 'ASSEMBLY' | 'DESIGNED PART' | 'STANDARD PART';

    // Revision-specific data
    revisionLevel: string;           // References PbsRevisionLevel.id
    partNumber: string;              // Manufacturer/internal part #
    partRevision: string;            // Part revision (e.g., "A", "Rev 2")
    quantity: number;                // Qty for this specific revision

    // Carry-over tracking
    carriedOverFrom?: string;        // Previous PBS item ID
    reviewStatus: PbsReviewStatus;   // Review state
    reviewedBy?: string;             // User who confirmed
    reviewedDate?: string;           // ISO 8601 timestamp
    changeNotes?: string;            // What changed from previous

    // Design/manufacturing data
    frozen: boolean;
    status: 'draft' | 'active' | 'released' | 'obsolete' | 'not_designed' | 'design_in_progress' | 'designed' | 'ready_to_order' | 'ordered' | 'received' | 'assembled' | 'deprecated';
    owner: string;
    buildMethod?: string;
    supplier?: string;
    leadTimeWeeks?: number;
    unitCost?: number;
    comment?: string;

    // Hierarchy
    parentId?: string | null;
    children?: PbsItemRevision[];

    // Metadata Standards v2.0.0
    projectName: string;
    epochId: number;
    bomItemId?: number;              // Optional link to Master BOM (System Definition)
}

/**
 * Revision Level Metadata
 * Summary information about a revision
 */
export interface PbsRevisionMetadata {
    revisionId: string;
    totalItems: number;
    pendingReview: number;
    confirmed: number;
    modified: number;
    totalCost: number;
    completionPercentage: number;
}

/**
 * Revision Comparison
 * Shows differences between two revisions
 */
export interface PbsRevisionDiff {
    itemId: string;
    pbsNumber: string;
    name: string;
    changeType: 'added' | 'removed' | 'modified' | 'unchanged';
    changes: {
        field: string;
        oldValue: any;
        newValue: any;
    }[];
}

/**
 * Carry-Over Configuration
 * Settings for copying items between revisions
 */
export interface CarryOverConfig {
    sourceRevisionId: string;
    targetRevisionId: string;
    includeChildren: boolean;
    copyPartNumbers: boolean;
    copyQuantities: boolean;
    markAllForReview: boolean;
}

export const REVISION_STAGE_LABELS: Record<PbsRevisionStage, string> = {
    'alpha-1': 'Alpha 1 (First Prototype)',
    'alpha-2': 'Alpha 2 (Second Prototype)',
    'beta-1': 'Beta 1 (Engineering Prototype - DVT)',
    'beta-2': 'Beta 2 (Refined Engineering - PVT)',
    'c-level': 'C-Level (Customer Samples)',
    'p-level': 'P-Level (Production Intent)',
};

export const REVISION_STATUS_LABELS: Record<PbsRevisionStatus, string> = {
    'planning': 'Planning',
    'building': 'Building',
    'testing': 'Testing',
    'complete': 'Complete',
    'archived': 'Archived',
};

export const REVIEW_STATUS_COLORS = {
    'pending': {
        border: 'border-red-500 dark:border-red-400',
        bg: 'bg-red-50 dark:bg-red-900/20',
        text: 'text-red-700 dark:text-red-300',
        badge: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    },
    'confirmed': {
        border: 'border-green-500 dark:border-green-400',
        bg: 'bg-green-50 dark:bg-green-900/20',
        text: 'text-green-700 dark:text-green-300',
        badge: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    },
    'modified': {
        border: 'border-yellow-500 dark:border-yellow-400',
        bg: 'bg-yellow-50 dark:bg-yellow-900/20',
        text: 'text-yellow-700 dark:text-yellow-300',
        badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    },
};
